// src/services/costs/calculations.js
// Berechnungslogik für BKP-Totale

/**
 * @param {import('./types').BkpNode} node
 * @returns {number}
 */
export function calculateTotal(node) {
  const {
    unit,
    unitPrice,
    length = 0,
    width = 0,
    height = 0,
    quantity = 0,
    factor = 1,
  } = node;

  if (!unit) return 0;

  // factor = 0 bleibt 0 (Position temporaer deaktivieren). Nur null/undefined -> 1.
  const effectiveFactor = factor ?? 1;

  // pauschal: Ausmass (L/B/H/Menge) wird ignoriert - nur EP x Faktor
  if (unit === "pl") {
    return Math.round(unitPrice * effectiveFactor * 100) / 100;
  }

  // Ohne jede Menge (L/B/H/qty alle 0) ergibt sich kein Total - sonst wuerde
  // der Default-"mit 1 multiplizieren" silent (unitPrice * factor) zurueckgeben.
  if (length <= 0 && width <= 0 && height <= 0 && quantity <= 0) {
    return 0;
  }

  const baseQuantity =
    (length > 0 ? length : 1) *
    (width > 0 ? width : 1) *
    (height > 0 ? height : 1) *
    (quantity > 0 ? quantity : 1);

  return Math.round(baseQuantity * unitPrice * effectiveFactor * 100) / 100;
}

/**
 * @param {import('./types').BkpTree} tree
 * @returns {import('./types').BkpTree}
 */
export function aggregateTree(tree) {
  const updated = { ...tree };

  // Hilfsfunktion: Sammle alle aktiven Nachkommen (rekursiv)
  const getActiveDescendantsTotal = (node) => {
    if (node.active) {
      // Wenn dieser Node aktiv ist, gib sein Total zurück
      return node.totalComputed;
    }

    // Wenn inaktiv, durchsuche alle Kinder rekursiv
    let sum = 0;
    const children = node.children || [];
    for (const childCode of children) {
      const child = updated[childCode];
      if (child) {
        sum += (getActiveDescendantsTotal(child) || 0);
      }
    }
    return sum;
  };

  // Bottom-up Aggregation
  for (let level = 5; level >= 1; level--) {
    const nodesAtLevel = Object.values(updated).filter(
      (n) => n.level === level
    );

    for (const node of nodesAtLevel) {
      if (level < 5) {
        const children = node.children || [];

        // WENN dieser Node AKTIV ist -> eigene Berechnung verwenden, Kinder ignorieren
        if (node.active) {
          // totalComputed bleibt wie berechnet, Kinder werden ignoriert
          continue;
        }

        // WENN dieser Node INAKTIV ist -> summiere rekursiv alle aktiven Nachkommen
        let sum = 0;
        for (const childCode of children) {
          const child = updated[childCode];
          if (child) {
            sum += (getActiveDescendantsTotal(child) || 0);
          }
        }

        updated[node.code] = {
          ...node,
          totalComputed: sum,
        };
      }
    }
  }

  return updated;
}

/**
 * @param {import('./types').BkpNode} node
 * @param {import('./types').BkpTree} tree
 * @returns {boolean}
 */
export function shouldChildrenBeDisabled(node, tree) {
  return node.active;
}

/**
 * Check if any ancestor in the chain is active (disabling all descendants).
 * Returns the code of the nearest active ancestor, or null.
 * @param {import('./types').BkpNode} node
 * @param {import('./types').BkpTree} tree
 * @returns {string|null}
 */
export function findActiveAncestor(node, tree) {
  let pc = node.parentCode;
  while (pc && tree[pc]) {
    if (tree[pc].active) return pc;
    pc = tree[pc].parentCode;
  }
  return null;
}

/**
 * @param {import('./types').BkpTree} tree
 * @returns {number}
 */
export function getProjectTotal(tree) {
  const level1Nodes = Object.values(tree).filter((n) => n.level === 1);
  return level1Nodes.reduce((sum, node) => {
    return sum + node.totalComputed;
  }, 0);
}
